import classnames from 'classnames'
import React from 'react'

import { CircleLoader } from '@driftt/tide-core'
import {
  Paginator,
  Table
} from '@driftt/tide-table'
import { useDimensions } from 'hooks/useDimensions'

import './style.css'

const ObjectList = ({
  isLoading,
  className,
  data,
  selectedColumns = [],
  setParams,
  params,
  primaryColumns,
  actionColumn,
  propertyResolver,
  error,
  paginationDisabled = false,
  initialLoadingText = "loading",
  errorText = "an error has occured",
  defaultCellClassNameResolver = () => null,
  tableClassName,
  ...rest
}) => {
  const [ref, dimensions]  = useDimensions()
  const showTable = data?.results?.length && !error
  const showError = error
  const initialLoad = !data && isLoading && !showError

  const updateSize = ({ value }) => {
    setParams(draft => {
      draft.size = value
    })
  }

  const goForward = () => {
    setParams(draft => {
      draft.from = Number(draft.from) + Number(draft.size)
    })
  }

  const goBackward = () => {
    setParams(draft => {
      draft.from = Number(draft.from) - Number(draft.size)
    })
  }

  const setSort = (value, direction) => {
    setParams(draft => {
      draft.sortField = value
      draft.sortAscended = !(direction === 'asc')
      draft.from = 0
    })
  }

  return (
    <>
      <div className={classnames("crm-object-list-wrapper", className)} ref={ref}>
        <div
          style={{ height: dimensions?.height ? (dimensions?.height) : `100%` }}
          className="crm-object-list">
          {initialLoad && (
            <div className="crm-object-list-loader">
              <CircleLoader size="large" className="crm-object-list-loader--animation" />
              <p>{initialLoadingText}</p>
            </div>
          )}
          {showError && (
            <div className="crm-object-list-loader crm-object-list-loader--error">
              <>
                <p>{errorText}</p>
                {isLoading && (
                  <>
                    <CircleLoader size="large" className="crm-object-list-loader--animation" />
                    <p>re-trying</p>
                  </>
                )}
              </>
            </div>
          )}
          {showTable && (
          <>
            <Table
              {...rest}
              isWorking={isLoading}
              className={classnames("crm-object-list-table--scroll", { "crm-object-list-table--loading": isLoading && !error }, tableClassName)}
              data={[
                ...data.results.map(objectRecord => ({
                  data: [
                    ...primaryColumns.map(({
                      value,
                      className,
                      primary = false
                    }, index) => ({
                      content: propertyResolver(value, objectRecord),
                      className: classnames(
                        'crm-object-list-cell--text',
                        'crm-object-list-sticky-column',
                        {
                          'crm-object-list-sticky-column--last': index === primaryColumns.length - 1,
                          'crm-object-list-sticky-column--primary': primary
                        },
                        className
                      )
                    })),
                    ...selectedColumns.map(({
                      value,
                      className
                    }) => ({
                      className: classnames('crm-object-list-cell--text', defaultCellClassNameResolver(value, objectRecord), className),
                      content: propertyResolver(value, objectRecord) || '--'
                    })),
                    ...(actionColumn ? [{
                      content: actionColumn.row.content(objectRecord),
                      className: classnames('crm-object-list-sticky-column',
                        'crm-object-list-sticky-column--right',
                        actionColumn.row.className),
                    }] : [])
                  ]
                })),
              ]}
              columns={[
                ...primaryColumns.map(({
                  label,
                  value,
                  sortable = true,
                  className,
                  primary = false,
                  ...rest
                }, index) => ({
                  label: label,
                  value: value,
                  sortable: sortable,
                  className: classnames(
                    'crm-object-list-header',
                    'crm-object-list-sticky-column',
                    className,
                    {
                      'crm-object-list-sticky-column--last': index === primaryColumns.length - 1,
                      'crm-object-list-sticky-column--primary': primary
                    }
                  ),
                  direction: params?.sortAscended ? 'asc' : 'desc',
                  sorting: params?.sortField === value,
                  onClick: sortable ? setSort : null,
                  ...rest
                })),
                ...selectedColumns.map(({
                  label,
                  value,
                  sortable = true,
                  className
                }) => ({
                  label,
                  value,
                  className: classnames('crm-object-list-header', className),
                  sortable: sortable,
                  direction: params?.sortAscended ? 'asc' : 'desc',
                  sorting: params?.sortField === value,
                  onClick: sortable ? setSort : null,
                  customKey: value
                })),
                ...(actionColumn ? [{
                  ...actionColumn.column,
                  customKey: 'action-column',
                  className: classnames(
                    'crm-object-list-sticky-column',
                    'crm-object-list-sticky-column--right',
                    'crm-object-list-header',
                    actionColumn.column.className
                  ),
                }] : [])
              ]}
            />
          </>
          )}
        </div>
      </div>
      { data && !paginationDisabled && (
        <Paginator
          position={''}
          className="crm-object-list-paginator"
          totalPages={Math.round(data?.total / params.size)}
          size={params.size}
          index={Math.round(params.from/params.size)}
          changeSize={updateSize}
          goForward={goForward}
          goBackward={goBackward}
        />
      )}
    </>
  )
}

export default ObjectList
